import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, TicketPercent } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Panel, BRL, inputCls, labelCls, btnPrimary, iconBtn, fmtDate } from "./ui";

const EMPTY = { code: "", kind: "percent", value: "", applies_to: "all", min_total: "", max_uses: "", valid_until: "" };
const swCls = "data-[state=checked]:bg-[#0A66C2] data-[state=unchecked]:bg-slate-200 [&>span]:bg-white";
const APPLIES = { all: "Casas e passeios", stay: "Só casas", tour: "Só passeios" };

export const CouponsAdmin = ({ items, onAdd, onUpdate, onDelete }) => {
  const [f, setF] = useState(EMPTY);
  const set = (p) => setF((x) => ({ ...x, ...p }));

  const submit = async () => {
    if (f.code.trim().length < 3 || !(Number(f.value) > 0)) return toast.error("Informe o código (mín. 3 letras) e o valor do desconto.");
    await onAdd({
      code: f.code.trim(), kind: f.kind, value: Number(f.value), applies_to: f.applies_to,
      min_total: Number(f.min_total || 0), max_uses: Number(f.max_uses || 0), valid_until: f.valid_until || null,
    });
    setF(EMPTY);
  };

  return (
    <Panel id="cupons" title="Cupons de desconto" subtitle="Crie códigos promocionais em % ou em reais. O uso é contado quando o PIX é pago." testId="coupons-admin">
      <div className="rounded-3xl bg-[#FBF8F3] border border-[#EDE6DA] p-4 grid grid-cols-2 lg:grid-cols-9 gap-3 items-end mb-5">
        <div className="col-span-2">
          <label className={labelCls}>Código</label>
          <input value={f.code} onChange={(e) => set({ code: e.target.value.toUpperCase().replace(/\s/g, "") })} placeholder="VERAO10" className={inputCls} data-testid="coupon-code" />
        </div>
        <div>
          <label className={labelCls}>Tipo</label>
          <select value={f.kind} onChange={(e) => set({ kind: e.target.value })} className={inputCls} data-testid="coupon-kind">
            <option value="percent">%</option>
            <option value="fixed">R$</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>{f.kind === "percent" ? "Desconto (%)" : "Desconto (R$)"}</label>
          <input type="number" value={f.value} onChange={(e) => set({ value: e.target.value })} className={inputCls} data-testid="coupon-value" />
        </div>
        <div className="col-span-2">
          <label className={labelCls}>Vale para</label>
          <select value={f.applies_to} onChange={(e) => set({ applies_to: e.target.value })} className={inputCls} data-testid="coupon-applies">
            {Object.entries(APPLIES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Mín. (R$)</label>
          <input type="number" value={f.min_total} onChange={(e) => set({ min_total: e.target.value })} placeholder="0" className={inputCls} data-testid="coupon-min" />
        </div>
        <div>
          <label className={labelCls}>Limite de usos</label>
          <input type="number" value={f.max_uses} onChange={(e) => set({ max_uses: e.target.value })} placeholder="sem limite" className={inputCls} data-testid="coupon-max-uses" />
        </div>
        <div>
          <label className={labelCls}>Válido até</label>
          <input type="date" value={f.valid_until} onChange={(e) => set({ valid_until: e.target.value })} className={inputCls} data-testid="coupon-valid-until" />
        </div>
        <button onClick={submit} className={`${btnPrimary} col-span-2 lg:col-span-9 lg:justify-self-end`} data-testid="coupon-save"><Plus size={15} /> Criar cupom</button>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-6">Nenhum cupom criado ainda.</p>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {items.map((c) => (
            <div key={c.id} className={`rounded-2xl border-2 border-dashed border-[#0A66C2]/30 p-4 ${c.active ? "" : "opacity-50"}`} data-testid={`coupon-${c.code}`}>
              <div className="flex items-center gap-2">
                <TicketPercent size={18} className="text-[#0A66C2]" />
                <span className="font-extrabold text-slate-900 tracking-wide">{c.code}</span>
                <div className="ml-auto flex items-center gap-1">
                  <Switch className={swCls} checked={c.active} onCheckedChange={(v) => onUpdate(c.id, { active: v })} data-testid={`coupon-toggle-${c.code}`} />
                  <button className={`${iconBtn} hover:text-red-600`} onClick={() => window.confirm(`Apagar o cupom ${c.code}?`) && onDelete(c.id)} data-testid={`coupon-delete-${c.code}`}><Trash2 size={14} /></button>
                </div>
              </div>
              <p className="text-2xl font-extrabold text-[#0A66C2] mt-1">{c.kind === "percent" ? `${c.value}% off` : `${BRL(c.value)} off`}</p>
              <p className="text-xs text-slate-500 mt-1">
                {APPLIES[c.applies_to]} · usado {c.uses}{c.max_uses ? `/${c.max_uses}` : ""} vez(es)
                {c.min_total ? ` · mín. ${BRL(c.min_total)}` : ""}{c.valid_until ? ` · até ${fmtDate(c.valid_until)}` : ""}
              </p>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};
